import { Component, AfterViewInit, OnDestroy, ViewChild, ElementRef, OnInit, Output, EventEmitter, NgZone, Inject, PLATFORM_ID, Renderer2 } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { EncriptacionService } from 'src/app/services/encriptacion.service';
import { URL_SERVICIOS } from 'src/app/config/config';
import { environment } from 'src/environments/environment.dev';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-medidor-signos',
  templateUrl: './medidor-signos.component.html',
  styleUrls: ['./medidor-signos.component.css']
})
export class MedidorSignosComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('saphereWrapper', { static: true }) saphereWrapper!: ElementRef<HTMLDivElement>;
  @Output() requestClose = new EventEmitter<void>();

  showIntro = true;
  pageInfo: any = {};
  buttonDisabled = true;
  isLoading = true;

  private SaphereScan: any = null;
  private saphereInstance: any = null;
  private destroyed = false;

  private urlSaphere = environment.URL_SAPHERE;
  private customizationId = environment.CUSTOMIZATION_ID;

  private desiredVariables: string[] = [];

  constructor(
    private renderer: Renderer2,
    private ngZone: NgZone,
    private http: HttpClient,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.pageInfo = new EncriptacionService().getPageInfo();
    console.log(this.pageInfo);
  }

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    this.clearWrapper();

    try {
      await this.loadSaphereModule();

      if (this.destroyed) return;

      this.ngZone.run(() => {
        this.buttonDisabled = false;
        this.isLoading = false;
      });
    } catch (error) {
      console.error('[Saphere v2] Error cargando módulo:', error);

      this.ngZone.run(() => {
        this.buttonDisabled = true;
        this.isLoading = false;
      });
    }
  }

  async proceedToMeter(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    this.showIntro = false;
    this.isLoading = true;

    try {
      /*
      |--------------------------------------------------------------------------
      | CARGAR MÓDULO SAPHERE
      |--------------------------------------------------------------------------
      */
      if (!this.SaphereScan) {
        await this.loadSaphereModule();
      }

      /*
      |--------------------------------------------------------------------------
      | OBTENER CONFIGURACIÓN ALMAMEDIS
      |--------------------------------------------------------------------------
      | Aquí obtenemos:
      | desiredVariables
      | según la configuración del cliente.
      */

      await this.getMeasureConfiguration();
      /*
      |--------------------------------------------------------------------------
      | INICIAR SAPHERE
      |--------------------------------------------------------------------------
      */
      await this.initSaphere();
    }
    catch (error) {

      console.error(
        '[Saphere v2] Error inicializando medidor:',
        error
      );

      this.isLoading = false;
    }

  }

  closeIntro(): void {
    this.proceedToMeter();
  }

  async closeMeter(): Promise<void> {
    this.destroySaphere();
    this.requestClose.emit();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroySaphere();
  }

  private loadSaphereModule(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    const win = window as any;

    if (win.IVirtual?.SaphereScan) {
      this.SaphereScan = win.IVirtual.SaphereScan;
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const readyHandler = (event: any) => {
        const SaphereScan = event?.detail?.SaphereScan || win.IVirtual?.SaphereScan;

        if (!SaphereScan) {
          reject(new Error('SaphereScan no fue encontrado después de cargar el módulo.'));
          return;
        }

        this.SaphereScan = SaphereScan;
        resolve();
      };

      window.addEventListener('saphere-scan:ready', readyHandler, { once: true });

      const existingScript = this.doc.getElementById('saphere-scan-v2') as HTMLScriptElement | null;

      if (existingScript) {
        return;
      }

      const script = this.renderer.createElement('script') as HTMLScriptElement;
      script.id = 'saphere-scan-v2';
      script.type = 'module';
      script.src = this.buildSaphereModuleUrl();

      script.onerror = (error: any) => {
        window.removeEventListener('saphere-scan:ready', readyHandler);
        reject(error);
      };

      this.renderer.appendChild(this.doc.body, script);
    });
  }

  private buildSaphereModuleUrl(): string {
    if (!this.customizationId) {
      return this.urlSaphere;
    }

    const separator = this.urlSaphere.includes('?') ? '&' : '?';

    return `${this.urlSaphere}${separator}customizationId=${encodeURIComponent(this.customizationId)}`;
  }

  private async initSaphere(): Promise<void> {
    if (!this.SaphereScan) {
      throw new Error('SaphereScan aún no está disponible.');
    }

    const wrapper = this.saphereWrapper?.nativeElement;

    if (!wrapper) {
      throw new Error('No se encontró el contenedor de Saphere.');
    }

    this.destroySaphere();
    wrapper.innerHTML = '';

    const newContainer = this.renderer.createElement('div') as HTMLDivElement;
    newContainer.id = 'saphere-container';
    newContainer.style.width = '100%';
    newContainer.style.height = '100%';

    this.renderer.appendChild(wrapper, newContainer);

    const options: any = {
      lang: 'es',

      proxy: {
        retrieveAccessToken: {
          strategy: 'delegate',
          url: `${URL_SERVICIOS}/saphere/access-token/${this.pageInfo.cus_id}`
        }
      },
      createMeasureOptions: {
        desiredVariables:
          this.desiredVariables
      },
      onEvent: (event: any) => {
        this.handleSaphereEvent(event);
      }
    };

    try {
      this.saphereInstance = this.SaphereScan.create(newContainer, options);

      await this.ngZone.runOutsideAngular(async () => {
        await this.saphereInstance.bootstrap();
      });

    } catch (error) {
      this.saphereInstance = null;
      throw error;
    }
  }

  private handleSaphereEvent(event: any): void {
    if (!event?.type) return;

    switch (event.type) {
      case 'widget:ready':
        this.ngZone.run(() => {
          this.isLoading = false;
          this.buttonDisabled = false;
        });
        break;

      case 'measure:result':
        this.handleMeasureResult(event);
        break;

      case 'measure:aborted':
        this.handleMeasureAborted(event);
        break;

      case 'measure:failed':
        console.error('[Saphere v2] Medición fallida:', event.code);
        break;

      case 'token:error':
        console.error('[Saphere v2] Error obteniendo access token:', event.code);
        break;

      case 'camera:error':
        console.error('[Saphere v2] Error de cámara:', event.code, event.name);
        break;

      case 'camera:ended':
        console.error('[Saphere v2] La cámara dejó de estar disponible.');
        break;

      case 'detector:error':
        console.error('[Saphere v2] Error cargando detector facial.');
        break;

      case 'widget:error':
        console.error('[Saphere v2] Error del widget:', event.message);
        break;
    }
  }

  private handleMeasureResult(event: any): void {
    const result = event?.result;

    if (!result) return;

    // El resultado de la medición llega aquí.
    // NO cierro automáticamente el componente porque Saphere v2
    // muestra posteriormente la pantalla de resultados.
    //
    // Si quieres cerrar inmediatamente después de medir:
    //
    // this.ngZone.run(() => this.requestClose.emit());
  }

  private handleMeasureAborted(event: any): void {
    if (!event?.code) return;

    if (event.code === 'CANCELED') {
      return;
    }

    if (event.code === 'CANCELED_WHILE_PROCESSING') {
      return;
    }

    console.warn('[Saphere v2] Medición abortada:', event.code);
  }

  private destroySaphere(): void {
    if (this.saphereInstance) {
      try {
        this.saphereInstance.destroy();
      } catch (error) {
        console.error('[Saphere v2] Error destruyendo instancia:', error);
      }

      this.saphereInstance = null;
    }

    this.clearWrapper();
  }

  private clearWrapper(): void {
    const host = this.saphereWrapper?.nativeElement;

    if (!host) return;

    while (host.firstChild) {
      host.removeChild(host.firstChild);
    }
  }

  private async getMeasureConfiguration(): Promise<void> {
    try {
      const response: any = await this.http
        .post(
          `${URL_SERVICIOS}/getCreatedMeasure/${this.pageInfo.cus_id}`,
          {}
        )
        .toPromise();
      if (!response?.status) {
        throw new Error(
          response?.datos?.msj ||
          'No fue posible obtener la configuración de Saphere'
        );
      }

      this.desiredVariables =
        response?.data?.desiredVariables || [];
      console.log(
        '[Saphere v2] Variables habilitadas:',
        this.desiredVariables
      );
    }
    catch (error) {
      console.error(
        '[Saphere v2] Error obteniendo configuración:',
        error
      );
      throw error;
    }
  }
}